import * as d3 from "d3";
import {
  TimeSeriesChart,
  percentChange,
  monthDayYear,
  placeTooltip,
  pageXY,
  throttle,
  unixDateRange,
  maxLabelSize,
} from "@aodin/charts";

function parseArray(d) {
  // x, z, y
  return {
    x: d3.isoParse(d[0]),
    y: d[2],
    z: d[1],
  };
}

const MEDIAN_COLOR = "#d442f5";
const MEAN_COLOR = "#bb5ff5";

class ComparisonChart extends TimeSeriesChart {
  yFormat = d3.format("+,~%");

  constructor(data, { preferLog = false, disableAnimations = false } = {}) {
    super(data, parseArray);

    // Save a list of all X values
    this.allX = unixDateRange(...this.xDomain);

    if (disableAnimations) this.noAnimation();
    this.screenHeightPercent(0.65);
    this.useContinuousScheme(d3.interpolateOranges, 0.2, 0.9);
    this.yAxisRight();

    this.config.HIGHLIGHT_STROKE_WIDTH = 2.0; // Width when highlighted
    this.config.STROKE_WIDTH = 1.5; // Width when not highlighted

    // Use a "show" parameter
    const params = new URLSearchParams(window.location.search);
    const coins = new Set(params.getAll("c"));

    // Only filter if there is at least one param
    if (coins.size) {
      data.columns.forEach((coin) => {
        if (coins.has(coin)) {
          // show
        } else {
          this.hidden.add(coin);
        }
      });
    }

    // Always add the median and mean to the data. The number of visible categories
    // and the dropdown checkbox toggles will determine their visibility
    // TODO Start with zero values for statisstics?
    const medians = this.calculateMedian()

    this.medianOffset = this.data.length;
    this.meanOffset = this.medianOffset + medians.length;
    this.data = [...this.data, ...medians, ...this.calculateMean()];

    // Add the mean and median colors
    this.colors.domain([...this.colors.domain(), "Median", "Mean"]);
    this.colors.range([...this.colors.range(), MEDIAN_COLOR, MEAN_COLOR]);

    // Set dash array patterns
    this.setPattern("Median", [0, 2, 0]);
    this.setPattern("Mean", [0, 2, 0]);

    // Toggle state attributes
    this.showMedian = true;
    this.showMean = true;
  }

  get statsData() {
    // Data to be used for calculating stats - not including the stats!
    return this.visibleData.filter((d) => d.z !== "Mean" && d.z !== "Median");
  }

  // Never use the stats data to calculate the y extent
  get yDomain() {
    // The y-axis should always include zero
    let min, max;
    if (this.visibleData.length) {
      [min, max] = d3.extent(d3.map(this.statsData, (d) => d.y));
    } else {
      [min, max] = d3.extent(d3.map(this.data, (d) => d.y));
    }
    if (min > 0) {
      min = 0;
    }
    if (max < 0) {
      max = 0;
    }
    return [min, max];
  }

  get yDomainMax() {
    return d3.extent(d3.map(this.data, (d) => d.y));
  }

  // Scale that uses all available data values - useful for initial layout
  get yScaleMax() {
    return d3
      .scaleLinear()
      .domain(this.yDomainMax)
      .range([this.layout.innerHeight, 0]);
  }

  updateLayout() {
    // Scales are needed to calculate the axes size, which may change layout and
    // require scales to be re-calculated
    const [xLabelWidth, xLabelHeight] = maxLabelSize(
      this.svg,
      this.layout,
      this.xScale,
      this.xFormat,
      "x axis",
    );
    this.layout.pad.bottom = d3.max([this.layout.pad.bottom, xLabelHeight]);

    const [yLabelWidth, yLabelHeight] = maxLabelSize(
      this.svg,
      this.layout,
      this.yScaleMax,
      this.yFormat,
      "y axis",
    );

    if (this.config.Y_AXIS_RIGHT) {
      this.layout.pad.right = d3.max([
        this.layout.pad.right,
        yLabelWidth + this.config.MARGIN_TICK + 5,
      ]);
      // The default axes has a pretty large left pad - if using a right axes this
      // left pad can be reduced
      this.layout.pad.left = 20;
    } else {
      this.layout.pad.left = d3.max([
        this.layout.pad.left,
        yLabelWidth + this.config.MARGIN_TICK + 5,
      ]);
    }
  }

  calculateMean() {
    const byX = d3.group(this.statsData, (d) => d.x);
    const means = new Map(
      Array.from(this.allX, (x) => [x, d3.mean(byX.get(x) || [], (d) => d.y) || 0]),
    );
    return Array.from(means, ([x, y]) => ({
      x,
      y,
      z: "Mean",
    }));
  }

  calculateMedian() {
    const byX = d3.group(this.statsData, (d) => d.x);
    const medians = new Map(
      Array.from(this.allX, (x) => [x, d3.median(byX.get(x) || [], (d) => d.y) || 0]),
    );
    return Array.from(medians, ([x, y]) => ({
      x,
      y,
      z: "Median",
    }));
  }

  update(x, y) {
    // Always re-calculate new median and mean values
    const median = this.calculateMedian();
    this.data.splice(this.medianOffset, median.length, ...median);

    const mean = this.calculateMean();
    this.data.splice(this.meanOffset, mean.length, ...mean);

    // Determine if the mean and median should be shown
    if (this.zVisible.length < 2) {
      this.hidden.add("Median");
      this.hidden.add("Mean");
    } else {
      if (this.showMedian) {
        this.hidden.delete("Median");
      } else {
        this.hidden.add("Median");
      }
      if (this.showMean) {
        this.hidden.delete("Mean");
      } else {
        this.hidden.add("Mean");
      }
    }

    // Update data in-place
    this.paths.data(d3.group(this.data, (d) => d.z));

    super.update(x, y);
  }

  parseData(data, parse) {
    // Values are an array of arrays: [date, slug, value]
    return d3.map(data.values, parse);
  }

  parseItems(data) {
    return data.columns;
  }

  render(selector) {
    super.render(selector);

    // Show everything when the chart body is double clicked
    document.querySelector(selector).addEventListener("dblclick", (evt) => {
      tooltip.style("visibility", "hidden");
      document.querySelectorAll("#legend > div").forEach((elem) => {
        elem.style.setProperty("opacity", 1.0);
      });
      this.showAll();
      evt.preventDefault();
    });

    // Create tooltip
    const tooltip = d3
      .select("body")
      .append("div")
      .attr("id", "comparison-tooltip")
      .attr("class", "d3-tooltip")
      .style("pointer-events", "none")
      .style("position", "absolute")
      .style("visibility", "hidden");

    const rect = document.querySelector(selector);
    const bbox = document.getElementById("comparison-tooltip");

    const onLeave = () => {
      this.noHighlight();
      tooltip.style("visibility", "hidden");
    };

    function onMove(d, evt) {
      this.highlight(d.z);
      const x = monthDayYear(d.x);
      const pct = percentChange(d.y);
      tooltip.html(`<strong>${d.z}</strong></br><em>${x}</em></br>${pct}`);
      const pad = evt.touchType ? 20 : 5;
      const [tx, ty, align] = placeTooltip(rect, bbox, d.dx, d.dy, pad);
      tooltip
        .style("visibility", null)
        .style("text-align", align)
        .style("left", `${tx}px`)
        .style("top", `${ty}px`);
    }

    this.onEvent(onMove, onLeave);
  }

  onEvent(move, leave) {
    const pointermove = (evt) => {
      if (evt.touches) {
        // Prevent scroll on touch devices
        evt.preventDefault();
        evt = evt.touches[0];
      }

      // X and y scales use the inner element, which is padded
      let [xm, ym] = d3.pointer(evt, this.gInner.node());

      // Determine the closest point to the cursor
      // TODO Points could be memoized based on hidden z items
      const points = d3.map(this.data, (d) => {
        if (this.hidden.has(d.z)) return null;
        return Math.hypot(this.x(d.x) - xm, this.y(d.y) - ym);
      });

      // Also add the percent data
      // const medianPoints = d3.map(this.medianValues, (d) => {
      //   return Math.hypot(this.x(d.x) - xm, this.y(d.y) - ym);
      // });

      // points.push(...medianPoints);

      const index = d3.leastIndex(points);
      if (index === -1 || points[index] === null) return;

      // Exit early if no point was found
      if (typeof index === "undefined") return;

      // TODO To only trigger the callback when the index changes
      // if (prevIndex && prevIndex == index) return;

      // const combined = [...this.data, ...this.medianValues];
      const d = this.data[index];
      this.placeDot(d.x, d.y, d.z);

      // Data that will be provided to the callback: include both the coordinates
      // of the dot and the pointer, both in relation to the page
      const [dx, dy] = pageXY(this.dot.node());
      const [px, py] = d3.pointer(evt, null);

      const data = {
        x: d.x,
        y: d.y,
        z: d.z,
        // Dot page coordinates
        dx: dx,
        dy: dy,
        // Pointer page coordinates
        px: px,
        py: py,
      };

      if (move) {
        move.call(this, data, evt);
      }
    };

    const pointerleave = (evt) => {
      this.hideDot();
      if (leave) {
        leave.call(this, evt);
      }
    };

    // Separate mouse and touch events
    this.svg
      .on("mousemove", throttle(pointermove, 1000.0 / this.config.FPS))
      .on("mouseleave", pointerleave)
      .on("touchstart", pointermove, { passive: false })
      .on("touchmove", throttle(pointermove, 1000.0 / this.config.FPS), {
        passive: false,
      })
      .on("touchend", pointerleave, { passive: false });
  }

  placeDot(x, y, z) {
    this.dot
      .style("display", null)
      .attr("transform", `translate(${this.x(x)},${this.y(y)})`);
    this.circle.attr("fill", z === "Median" ? MEDIAN_COLOR : this.colors(z));
  }

  createLegend(elem) {
    if (!this.items) return;
    // Create a legend with a item for each category built via template
    const legend = document.querySelector(elem);
    const template = document.querySelector("#legend-item");

    this.items.forEach((token) => {
      const clone = template.content.cloneNode(true);
      const div = clone.querySelector("div");
      if (this.hidden.has(token)) {
        div.style.setProperty("opacity", 0.4);
      }

      // TODO Disable legend events for now - use the tagify input
      // div.addEventListener("dblclick", (evt) => {
      //   // Show only the selected token
      //   document.querySelectorAll("#legend > div").forEach((elem) => {
      //     elem.style.setProperty("opacity", 0.4);
      //   });

      //   // Leave the current legend item visible
      //   div.style.setProperty("opacity", 1.0);

      //   // Update the hidden params and re-calculate the chart
      //   this.hidden = new Set(this.Z);
      //   this.hidden.delete(token);
      //   this.toggle();
      //   evt.preventDefault();
      // });

      // div.addEventListener("click", (evt) => {
      //   if (evt.detail > 1) {
      //     // Prevent double-clicks (or more) from firing this event
      //     return;
      //   }

      //   if (this.hidden.has(token)) {
      //     this.show(token);
      //     div.style.setProperty("opacity", 1.0);
      //     // this.updateParams();
      //     // this.calculate();
      //   } else {
      //     this.hide(token);
      //     div.style.setProperty("opacity", 0.4);
      //     // this.updateParams();
      //     // this.calculate();
      //   }
      // });

      div.addEventListener("click", (evt) => {
        // TODO Persist chart params?
        window.open(`${window.location.origin}/${token}`, '_blank');
      });

      clone.querySelector("rect").setAttribute("fill", this.colors(token));
      clone.querySelector("button").textContent = token;
      clone.querySelector("div").dataset.symbol = token;
      legend.appendChild(clone);
    });
  }

  toggle() {
    if (this.visibleData.length) {
      // Recalculate the y-scale based on the non-hidden items
      this.y = this.yScale;
    } else {
      // Set y-axis to zero
      // We would need the last y-axis domain in order to prevent tick labels
      // from changing during this transition
      this.y = d3
        .scaleLinear()
        .domain(this.y.domain())
        .range([this.layout.innerHeight, this.layout.innerHeight]);
    }
    this.update(this.x, this.y);

    document.querySelectorAll("#legend > div").forEach((elem) => {
      if (this.hidden.has(elem.dataset.symbol)) {
        elem.style.setProperty("opacity", 0.4);
      } else {
        elem.style.setProperty("opacity", 1.0);
      }
    });
  }
}

export function Comparison(data, options) {
  return new ComparisonChart(data, options);
}
